/**
 * User-facing error sanitization (brand containment).
 *
 * FutureX never reveals its underlying model provider — success paths already
 * present `fx-*` tiers only, but upstream FAILURES leak the provider verbatim:
 * `@fim/ai-sdk` builds messages like `deepseek request failed (402):
 * Insufficient Balance`, and those flow straight into SSE `error` frames and
 * the HTTP error envelope. Every surface that shows an error to an end user
 * (gateway stream, portal chat, public completions API, CLI) must route the
 * message through {@link sanitizeAiErrorMessage} first.
 *
 * Logs are intentionally NOT sanitized — operators need the real provider
 * error; only the client-visible message is rebranded.
 */

/**
 * The provider account itself ran out of credit (DeepSeek 402 "Insufficient
 * Balance"). That is an OPERATOR problem — showing "insufficient balance" to an
 * end user reads as "top up your FutureX account", which is wrong and, during
 * the free launch, not even possible. Present it as temporary unavailability.
 */
const UPSTREAM_BALANCE_PATTERN = /insufficient\s+(balance|funds|quota|credit)/i;

const UPSTREAM_UNAVAILABLE_MESSAGE =
  'FutureX is temporarily unavailable (the service is at capacity). Please try again in a few minutes.';

/**
 * Rebrand a raw error message for end users: provider names/URLs become
 * FutureX, and an upstream out-of-credit error becomes a temporary-outage
 * notice. Idempotent and safe on any string (non-matching text passes through).
 */
export function sanitizeAiErrorMessage(message: string): string {
  if (UPSTREAM_BALANCE_PATTERN.test(message)) return UPSTREAM_UNAVAILABLE_MESSAGE;
  return (
    message
      // Provider URLs first (an api.deepseek.com URL must not survive as a
      // half-rewritten fake domain after the name replacement below).
      .replace(/https?:\/\/[^\s"')]*deep\s*seek[^\s"')]*/gi, 'the FutureX API')
      .replace(/deep\s*seek/gi, 'FutureX')
  );
}

/** {@link sanitizeAiErrorMessage} for a thrown value of unknown shape. */
export function sanitizeAiError(err: unknown): string {
  return sanitizeAiErrorMessage(err instanceof Error ? err.message : String(err));
}
